/**
 * @file Bannière globale du dashboard sur l'état de l'abonnement (grâce, suspension, fin d'essai).
 * @module packages.common.components.subscription-notice-banner.SubscriptionNoticeBanner
 *
 * Affichée en tête de chaque page du dashboard client, parce que l'établissement ne
 * visite jamais Réglages > Modules : sans elle, il découvre la coupure quand le menu
 * du module disparaît. Les messages rouges (paiement) ne se ferment pas ; le message
 * orange (essai qui se termine) se ferme pour la journée.
 *
 * Réservée aux utilisateurs qui peuvent agir (permission des réglages généraux,
 * celle de la page Modules) : le personnel de salle n'a ni à voir l'état de
 * facturation ni de bouton qui mène nulle part.
 */
import {useMemo, useState} from 'react';
import {Alert, Button, IconButton, Stack, Typography} from '@mui/material';
import {useActiveCompany, usePermissions} from 'packages/common/hooks/session';
import {useAllSubscriptions} from 'packages/common/hooks/api/app/subscriptions/Subscriptions';
import {usePaymentMethods} from 'packages/common/hooks/api/app/subscriptions/PaymentMethods';
import {useRouter} from 'packages/common/routes/hooks';
import {paths} from 'packages/common/routes/paths';
import {fDateValidity, fReservationTime} from 'packages/common/utils/formatTime';
import {localStorageAvailable} from 'packages/common/utils/storageAvailable';
import {resolveSubscriptionNotice, SUBSCRIPTION_NOTICE_KINDS} from 'packages/common/utils/subscriptionAccessHelpers';
import Iconify from 'packages/common/components/iconify';

const DISMISS_STORAGE_PREFIX = 'brydge_subscription_notice_dismissed';

// Permission qui ouvre Réglages > Modules, cible du bouton d'action
const BILLING_FEATURE_KEY = 'settings.general';

const todayKey = () => new Date().toISOString().slice(0, 10);

const dismissKey = (companyId, subscriptionId) => `${DISMISS_STORAGE_PREFIX}_${companyId}_${subscriptionId}`;

const readDismissedToday = (companyId, subscriptionId) => {
  if (!localStorageAvailable()) return false;
  return localStorage.getItem(dismissKey(companyId, subscriptionId)) === todayKey();
};

const moduleNameOf = (subscription) => subscription?.moduleId?.name || subscription?.planId?.moduleId?.name || 'votre module';

/**
 * Construit le contenu (sévérité, texte, action) du message à afficher.
 *
 * @param {{kind: string, subscription: Object}} notice - Message résolu
 * @param {string|null} timezone - Fuseau de l'établissement
 * @return {{severity: string, message: string, actionLabel: string, dismissible: boolean}} Contenu
 */
const buildContent = (notice, timezone) => {
  const moduleName = moduleNameOf(notice.subscription);
  const {accessGrace, currentPeriodEnd} = notice.subscription;

  switch (notice.kind) {
    case SUBSCRIPTION_NOTICE_KINDS.SUSPENDED:
      return {
        severity: 'error',
        dismissible: false,
        actionLabel: 'Régulariser le paiement',
        message: `Module ${moduleName} suspendu faute de paiement : régularisez pour le rétablir immédiatement.`,
      };
    case SUBSCRIPTION_NOTICE_KINDS.GRACE: {
      const endsAt = accessGrace?.endsAt;
      const when = endsAt ? ` le ${fDateValidity(endsAt, timezone)} à ${fReservationTime(endsAt, 'fr', timezone)}` : ' prochainement';
      return {
        severity: 'error',
        dismissible: false,
        actionLabel: 'Régulariser le paiement',
        message: `Paiement en échec : le module ${moduleName} sera suspendu${when} si le paiement n'est pas régularisé.`,
      };
    }
    case SUBSCRIPTION_NOTICE_KINDS.TRIAL_EXPIRED:
      return {
        severity: 'error',
        dismissible: false,
        actionLabel: 'Découvrir les offres',
        message: `Votre essai du module ${moduleName} est terminé : choisissez une formule pour retrouver l'accès.`,
      };
    case SUBSCRIPTION_NOTICE_KINDS.TRIAL_ENDING:
      return {
        severity: 'warning',
        dismissible: true,
        actionLabel: 'Activer avec ma carte',
        message: `Votre essai du module ${moduleName} se termine le ${fDateValidity(currentPeriodEnd, timezone)} : ajoutez une carte pour continuer sans interruption.`,
      };
    default:
      return null;
  }
};

/**
 * Bannière d'état de l'abonnement, en tête du contenu du dashboard.
 *
 * @return {JSX.Element|null} La bannière, ou null quand il n'y a rien à signaler
 */
export const SubscriptionNoticeBanner = () => {
  const {companyId, timezone} = useActiveCompany();
  const {hasFeatureAccess} = usePermissions();
  const router = useRouter();
  // Sans la permission, aucune requête ne part : les hooks acceptent un identifiant nul
  const canManageBilling = hasFeatureAccess(BILLING_FEATURE_KEY) === true;
  const {subscriptions} = useAllSubscriptions(canManageBilling ? companyId : null);

  // Les cartes ne sont lues (appel Stripe côté serveur) que si un essai offert est en cours
  const hasTrialGrant = useMemo(
      () => subscriptions.some((sub) => sub.isManualGrant && sub.grantType === 'trial' && sub.status === 'active'),
      [subscriptions],
  );
  const {paymentMethods, isLoading: isLoadingCards, isError: isCardsError} = usePaymentMethods(hasTrialGrant ? companyId : null);

  const [dismissedId, setDismissedId] = useState(null);

  const notice = useMemo(() => {
    // Tant que les cartes ne sont pas connues, ou si leur lecture a échoué, on ne
    // juge pas un essai « sans carte » : mieux vaut se taire que réclamer une carte déjà enregistrée
    if (hasTrialGrant && (isLoadingCards || isCardsError)) return null;
    return resolveSubscriptionNotice({subscriptions, hasPaymentMethod: paymentMethods.length > 0});
  }, [subscriptions, hasTrialGrant, isLoadingCards, isCardsError, paymentMethods]);

  if (!canManageBilling || !notice) return null;

  const content = buildContent(notice, timezone);
  if (!content) return null;

  const subscriptionId = String(notice.subscription._id);
  const isDismissed = content.dismissible && (dismissedId === subscriptionId || readDismissedToday(companyId, subscriptionId));
  if (isDismissed) return null;

  const handleDismiss = () => {
    if (localStorageAvailable()) {
      localStorage.setItem(dismissKey(companyId, subscriptionId), todayKey());
    }
    setDismissedId(subscriptionId);
  };

  const handleAction = () => router.push(paths.app.settings.payment.modules);

  return (
    <Alert
      severity={content.severity}
      variant="filled"
      icon={<Iconify icon={content.severity === 'error' ? 'solar:danger-triangle-bold' : 'solar:clock-circle-bold'} width={22} />}
      action={
        // La prop `action` remplace la croix de MUI : on la rend nous-mêmes pour le message fermable
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleAction}
            sx={{whiteSpace: 'nowrap', fontWeight: 700, borderColor: 'rgba(255,255,255,0.6)'}}
          >
            {content.actionLabel}
          </Button>
          {content.dismissible && (
            <IconButton size="small" color="inherit" aria-label="Masquer pour aujourd'hui" onClick={handleDismiss}>
              <Iconify icon="mingcute:close-line" width={18} />
            </IconButton>
          )}
        </Stack>
      }
      sx={{
        'mb': 2,
        'borderRadius': 1.5,
        'alignItems': 'center',
        '& .MuiAlert-message': {flex: 1, py: 0.5},
        '& .MuiAlert-action': {pt: 0, alignItems: 'center', flexWrap: 'wrap', gap: 0.5},
      }}
    >
      <Typography variant="body2" sx={{fontWeight: 600}}>
        {content.message}
      </Typography>
    </Alert>
  );
};
